import React, { useState, useEffect } from 'react';
import { therapistApi } from '../../services/api';
import { Therapist } from '../../types';
import { handleApiError, handleSuccess, validateRequired, validateEmail, validatePhone, validateForm } from '../../utils/errorHandler';
import './TherapistForm.css';

interface TherapistFormProps {
  therapist?: Therapist | null;
  onSubmit: () => void;
  onCancel: () => void;
}

const TherapistForm: React.FC<TherapistFormProps> = ({ therapist, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    specialization: '',
    qualification: '',
    designation: '',
    email: '',
    phone: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});


  useEffect(() => {
    if (therapist) {
      setFormData({
        name: therapist.name,
        specialization: therapist.specialization || '',
        qualification: therapist.qualification || '',
        designation: therapist.designation || '',
        email: therapist.email || '',
        phone: therapist.phone || '',
        is_active: therapist.is_active
      });
    }
  }, [therapist]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const validateFormData = () => {
    const validationRules = {
      name: (value: string) => validateRequired(value, 'Name'),
      email: (value: string) => value ? validateEmail(value) : null,
      phone: (value: string) => value ? validatePhone(value) : null,
    };

    return validateForm(formData, validationRules);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateFormData();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (therapist) {
        await therapistApi.update(therapist.id, formData);
        handleSuccess('Therapist updated successfully');
      } else {
        await therapistApi.create(formData);
        handleSuccess('Therapist created successfully');
      }
      onSubmit();
    } catch (err: any) {
      handleApiError(err, therapist ? 'Failed to update therapist' : 'Failed to create therapist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{therapist ? 'Edit Therapist' : 'Add New Therapist'}</h3>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="therapist-form">
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className={`form-input ${errors.name ? 'error' : ''}`}
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="specialization">Specialization</label>
            <input
              type="text"
              id="specialization"
              name="specialization"
              value={formData.specialization}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Speech Therapy, Occupational Therapy"
            />
          </div>

          <div className="form-group">
            <label htmlFor="qualification">Qualification</label>
            <input
              type="text"
              id="qualification"
              name="qualification"
              value={formData.qualification}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Master's in Speech-Language Pathology, PhD in Psychology"
            />
          </div>

          <div className="form-group">
            <label htmlFor="designation">Designation</label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              className="form-input"
              placeholder="e.g., Senior Therapist, Lead Occupational Therapist"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`form-input ${errors.email ? 'error' : ''}`}
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`form-input ${errors.phone ? 'error' : ''}`}
            />
            {errors.phone && <span className="field-error">{errors.phone}</span>}
          </div>


          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              Active
            </label>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-button">
              {loading ? 'Saving...' : (therapist ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TherapistForm;